import {Mail, MailCheck, MousePointerClick} from "lucide-react";

import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {cn} from "@/lib/utils";

import type {DashboardEmails} from "../types";
import {DeltaPill} from "./delta-pill";
import {EmailByModelList} from "./email-by-model-list";
import {EmailTimeSeriesChart} from "./email-time-series-chart";

type EmailsSectionProps = {
  emails: DashboardEmails;
};

export function EmailsSection({emails}: EmailsSectionProps) {
  const {in_period: now, delta_pct} = emails;

  const cards = [
    {
      key: "delivered" as const,
      label: "Delivered",
      icon: Mail,
      value: now.delivered,
      delta: delta_pct.delivered,
    },
    {
      key: "opened" as const,
      label: "Opened",
      icon: MailCheck,
      value: now.opened,
      delta: delta_pct.opened,
    },
    {
      key: "clicked" as const,
      label: "Clicked",
      icon: MousePointerClick,
      value: now.clicked,
      delta: delta_pct.clicked,
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {cards.map(({key, ...card}) => (
          <EmailTotalCard key={key} {...card} />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <EmailTimeSeriesChart data={emails.time_series_local} />
        <EmailByModelList rows={emails.by_model} />
      </div>
    </div>
  );
}

function EmailTotalCard({
  label,
  icon: Icon,
  value,
  delta,
  className,
}: {
  label: string;
  icon: React.ElementType;
  value: number;
  delta: number | null;
  className?: string;
}) {
  return (
    <Card className={cn("gap-3 py-4", className)}>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-muted-foreground text-sm font-medium">{label}</CardTitle>
        <span className="bg-muted text-muted-foreground flex size-8 shrink-0 items-center justify-center rounded-md">
          <Icon className="size-4" />
        </span>
      </CardHeader>
      <CardContent className="flex items-end justify-between gap-2">
        <span className="text-foreground text-2xl font-semibold tabular-nums">
          {value.toLocaleString()}
        </span>
        <DeltaPill value={delta} />
      </CardContent>
    </Card>
  );
}
